import { z } from 'zod';
const countSchema = z.number().int().nonnegative();
const rateSchema = z.number().min(0).max(1).nullable();
export const emailMetricCountsSchema = z.object({
    sent: countSchema.describe('Emails that left Reach: every email without a pre-send error. Test sends are excluded.'),
    delivered: countSchema.describe('Sent emails the provider confirmed as delivered, or that show proof of receipt (opened, clicked, marked as spam, or unsubscribed).'),
    opens: countSchema.describe('Sent emails that were opened or clicked. A click counts as an open because opens are detected by a tracking image that many mail apps block, so a recipient can click a link without an open ever being recorded.'),
    clicks: countSchema.describe('Sent emails with at least one tracked link click. Each email counts once however many links were clicked, and unsubscribe links are not tracked.'),
    bounces: countSchema.describe('Sent emails that hard or soft bounced, counted once. An email can count as both delivered and bounced, for example when the provider reports a bounce after confirming delivery.'),
    spamComplaints: countSchema.describe('Sent emails the recipient marked as spam.'),
    unsubscribes: countSchema.describe('Sent emails the recipient unsubscribed from.'),
});
export const emailMetricRatesSchema = z.object({
    openRate: rateSchema.describe('opens ÷ delivered, between 0 and 1. Null when nothing was delivered.'),
    clickRate: rateSchema.describe('clicks ÷ opens, between 0 and 1. Null when nothing was opened.'),
    bounceRate: rateSchema.describe('bounces ÷ sent, between 0 and 1. Null when nothing was sent.'),
    spamRate: rateSchema.describe('spamComplaints ÷ delivered, between 0 and 1. Null when nothing was delivered.'),
    unsubscribeRate: rateSchema.describe('unsubscribes ÷ delivered, between 0 and 1. Null when nothing was delivered.'),
});
export const emailMetricsSchema = emailMetricCountsSchema.merge(emailMetricRatesSchema);
export const EMPTY_EMAIL_METRIC_COUNTS = {
    sent: 0,
    delivered: 0,
    opens: 0,
    clicks: 0,
    bounces: 0,
    spamComplaints: 0,
    unsubscribes: 0,
};
const rate = (numerator, denominator) => denominator > 0 ? numerator / denominator : null;
export const buildEmailMetrics = (counts) => ({
    ...counts,
    openRate: rate(counts.opens, counts.delivered),
    clickRate: rate(counts.clicks, counts.opens),
    bounceRate: rate(counts.bounces, counts.sent),
    spamRate: rate(counts.spamComplaints, counts.delivered),
    unsubscribeRate: rate(counts.unsubscribes, counts.delivered),
});
// TODO(ENG-4127): remove the renamed fields, hardBounces and errors once the frontend reads the shared names.
const legacyRateSchema = z.number().min(0).max(1);
export const emailStatisticsSchema = emailMetricsSchema.extend({
    numberSent: countSchema.describe('Deprecated: use sent.'),
    linksClicked: countSchema.describe('Deprecated: use clicks.'),
    openPercentage: legacyRateSchema.describe('Deprecated: use openRate. 0 when nothing was delivered.'),
    softBounceRate: legacyRateSchema.describe('Deprecated: use bounceRate. Bounces that were not hard bounces ÷ sent, between 0 and 1. softBounceRate + hardBounceRate equals bounceRate.'),
    hardBounceRate: legacyRateSchema.describe('Deprecated: use bounceRate. Hard bounces ÷ sent, between 0 and 1.'),
    spamComplaintRate: legacyRateSchema.describe('Deprecated: use spamRate. 0 when nothing was delivered.'),
    unsubscribedRate: legacyRateSchema.describe('Deprecated: use unsubscribeRate. 0 when nothing was delivered.'),
    errors: countSchema.describe('Deprecated. Emails that failed before leaving Reach, such as an invalid address or a recipient who previously bounced or unsubscribed. Test sends are excluded.'),
});
export const EMPTY_EMAIL_STATISTICS_COUNTS = {
    ...EMPTY_EMAIL_METRIC_COUNTS,
    hardBounces: 0,
    errors: 0,
};
export const buildEmailStatistics = ({ hardBounces, errors, ...counts }) => {
    const metrics = buildEmailMetrics(counts);
    return {
        ...metrics,
        numberSent: metrics.sent,
        linksClicked: metrics.clicks,
        openPercentage: metrics.openRate ?? 0,
        softBounceRate: rate(metrics.bounces - hardBounces, metrics.sent) ?? 0,
        hardBounceRate: rate(hardBounces, metrics.sent) ?? 0,
        spamComplaintRate: metrics.spamRate ?? 0,
        unsubscribedRate: metrics.unsubscribeRate ?? 0,
        errors,
    };
};
//# sourceMappingURL=email-metrics.js.map